/* eslint-disable i18next/no-literal-string */
import Countly from 'countly-sdk-web';
import { config } from '../config';

let initialized = false;

/**
 * Must be called once at app startup, before React renders.
 * Safe to call multiple times (idempotent) — guards against React Strict Mode
 * double-invocations and HMR re-executions.
 */
export function initCountly(): void {
  if (initialized) {
    return;
  }

  const appKey = config.COUNTLY_APP_KEY;
  const url = config.COUNTLY_URL;

  if (!config.COUNTLY_ENABLED || !appKey || !url) {
    if (config.IS_DEV) {
      console.warn(
        '[Countly] Countly is disabled or missing credentials; skipping init.',
      );
    }
    return;
  }

  try {
    Countly.init({
      app_key: appKey,
      url,
      debug: config.IS_LOCAL,
      app_version: config.RELEASE,
      session_update: 60,
      use_session_cookie: true,
    });

    Countly.track_sessions();
    Countly.track_errors();

    initialized = true;
  } catch (error) {
    console.error('[Countly] Initialization failed:', error);
  }
}

export function isCountlyReady(): boolean {
  return initialized;
}

/**
 * Track an SPA page view. Call on every route change.
 *
 * @param viewName - The route path or human-readable page name.
 * @param viewSegmentation - Optional key-value pairs attached to the view event.
 */
export function trackView(
  viewName: string,
  viewSegmentation?: Record<string, string>,
): void {
  if (!initialized) {
    return;
  }
  try {
    Countly.track_pageview(viewName, undefined, viewSegmentation);
  } catch (_) {
    // Analytics must never crash the app.
  }
}

/**
 * Record a custom event.
 *
 * @param key - Event name (e.g. "Meal Added").
 * @param segmentation - Arbitrary key-value metadata.
 * @param count - Number of occurrences (default 1).
 * @param sum - Numeric value to aggregate (e.g. dollar amount).
 */
export function trackEvent(
  key: string,
  segmentation?: Record<string, string | number | boolean>,
  count: number = 1,
  sum?: number,
): void {
  if (!initialized) {
    return;
  }
  try {
    Countly.add_event({
      key,
      count,
      sum,
      segmentation,
    });
  } catch (_) {
    // Silent fail.
  }
}

/**
 * Identify the current user and merge the anonymous session into their profile.
 * Call once after login / auth hydration.
 *
 * @param userId - Stable, unique user identifier (e.g. clinician ID).
 * @param merge - When true the prior anonymous session data is merged (default true).
 * @param userProperties - Optional properties written to the Countly user profile.
 */
export function identifyUser(
  userId: string,
  merge: boolean = true,
  userProperties?: Record<string, string>,
): void {
  if (!initialized) {
    return;
  }
  try {
    Countly.change_id(userId, merge);
    if (userProperties) {
      for (const [key, value] of Object.entries(userProperties)) {
        Countly.userData.set(key, value);
      }
      Countly.userData.save();
    }
  } catch (_) {
    // Silent fail.
  }
}

/**
 * Reset the device ID to a new SDK-generated value (no merge).
 * Call on logout to ensure the next visitor gets a clean anonymous session.
 */
export function resetUser(): void {
  if (!initialized) {
    return;
  }
  try {
    const anonId = 'anon-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    Countly.change_id(anonId, false);
  } catch (_) {
    // Silent fail.
  }
}

/**
 * Returns true if Countly has a persisted device ID from a previous session
 * that looks like a clinician ID (i.e. not an anonymous/SDK-generated one).
 * Used to detect stale sessions after token expiry or direct navigation to login.
 */
export function hasStaleUserSession(): boolean {
  if (!initialized) {
    return false;
  }
  try {
    const deviceId = Countly.get_device_id();
    if (!deviceId) {
      return false;
    }
    return !deviceId.startsWith('anon-');
  } catch (_) {
    return false;
  }
}

const SENSITIVE_KEY_PATTERN = /patient.?id|patientid/i;

/**
 * Sanitizes event properties into Countly-safe segmentation values.
 * - Strips keys matching patient identifier patterns to prevent PII leakage.
 * - Drops non-primitive values (objects, arrays) entirely to avoid forwarding
 *   raw API responses or other structures that may contain patient context.
 * - Keeps only safe scalars: strings, numbers, booleans.
 */
export function sanitizeSegmentation(
  properties: Record<string, any>,
): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Replaces numeric path segments with :id to prevent patient/user IDs
 * from leaking into analytics view names.
 * e.g. /patient/12345/summary → /patient/:id/summary
 */
export function parameterizePath(pathname: string): string {
  return pathname.replace(/\/\d+/g, '/:id');
}

/**
 * Manually report an error to Countly's crash analytics.
 * Automatic JS error tracking is already enabled via track_errors() in init,
 * but this allows explicit logging from catch blocks.
 */
export function logError(
  error: Error | string,
  segments?: Record<string, string>,
): void {
  if (!initialized) {
    return;
  }
  try {
    const err = typeof error === 'string' ? new Error(error) : error;
    Countly.log_error(err, segments);
  } catch (_) {
    // Silent fail.
  }
}
